// src/pages/Events/Events.js - UPDATING EXISTING FILE
import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { useLocation, useNavigate } from "react-router-dom";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { axiosInstance } from "../../utils/axios";
import { useSpace } from "../../contexts/SpaceContext";
import { stripHtml } from "../../utils/HTMLParser";
import ReusableTable from "../../components/Common/ReusableTable";

// import { format, utcToZonedTime } from "date-fns-tz";
// import { format } from "date-fns";
// import { utcToZonedTime } from "date-fns-tz";

// import { parseISO } from "date-fns";
// import { format, utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

import { format } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Checkbox,
  Chip,
  Divider as MuiDivider,
  Grid,
  IconButton,
  Paper as MuiPaper,
  Tooltip,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Card as MuiCard,
  CardContent,
  CardMedia as MuiCardMedia,
  Snackbar,
  Alert,
} from "@mui/material";
import { green, orange } from "@mui/material/colors";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";

import { spacing } from "@mui/system";

// Helper function to parse event dates
const parseEventDate = (dateValue) => {
  if (!dateValue) return null;

  if (dateValue instanceof Date) return dateValue;

  if (typeof dateValue === "string" && /^\d+$/.test(dateValue)) {
    return new Date(parseInt(dateValue, 10));
  }

  if (typeof dateValue === "number") {
    return new Date(dateValue);
  }

  if (typeof dateValue === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    const [year, month, day] = dateValue.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  return new Date(dateValue);
};

// Helper function to parse time-only strings and convert to display time
const parseTimeString = (timeStr, timezone = "UTC") => {
  if (!timeStr) return null;

  // If it's already a Date object, return it
  if (timeStr instanceof Date) return timeStr;

  // If it's a time-only string like "04:30:00"
  if (typeof timeStr === "string" && /^\d{1,2}:\d{2}(:\d{2})?$/.test(timeStr)) {
    const [hours, minutes, seconds = 0] = timeStr.split(":").map(Number);
    const date = new Date();
    date.setUTCHours(hours, minutes, seconds, 0);
    return date;
  }

  // Otherwise try to parse as regular date
  return new Date(timeStr);
};

const Card = styled(MuiCard)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

const CardMedia = styled(MuiCardMedia)`
  height: 220px;
`;

const EventCard = ({ data: event, selected: isSelected, onSelect }) => {
  const startDate = parseEventDate(event.startDate);
  const endDate = parseEventDate(event.endDate);

  const getEventStatus = (startDate, endDate) => {
    const now = new Date();
    if (now < startDate) return "upcoming";
    if (now > endDate) return "completed";
    return "ongoing";
  };

  const status = getEventStatus(startDate, endDate);

  return (
    <Paper>
      <Card>
        <div style={{ position: "relative" }}>
          <CardMedia
            image="https://d2xqcdy5rl17k2.cloudfront.net/images/default-landing-banner-min.png"
            title="Event banner"
            sx={{ height: 140 }}
          />
          <Checkbox
            checked={isSelected}
            onChange={onSelect}
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              background: "rgba(255,255,255,0.5)",
              borderRadius: "50%",
              padding: "4px",
              "&:hover": { background: "rgba(255,255,255,0.7)" },
            }}
          />
        </div>
        <CardContent>
          <Typography gutterBottom variant="h5" component="h2">
            {event.name}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            component="p"
            sx={{ minHeight: 60 }}
          >
            {event.about
              ? stripHtml(event.about).substring(0, 120) +
                (stripHtml(event.about).length > 120 ? "..." : "")
              : "No description"}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            {startDate.toLocaleDateString("en-US", {
              weekday: "short",
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })}
          </Typography>
          <Box
            mt={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Chip
              size="small"
              label={status}
              sx={{
                backgroundColor:
                  status === "upcoming"
                    ? green[500]
                    : status === "ongoing"
                    ? orange[700]
                    : "#888",
                color: "white",
              }}
            />
            <Typography variant="body2" color="textSecondary">
              {event.timezone}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Paper>
  );
};

function EventsTable({
  events,
  loading,
  viewStyle,
  setViewStyle,
  selected,
  setSelected,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  handleNotification,
  handleDeleteEvents,
  searchQuery,
  onSearchChange,
}) {
  const navigate = useNavigate();

  // Define table columns
  const columns = React.useMemo(
    () => [
      {
        field: "serialNumber",
        headerName: "S.No",
        width: 70,
        sortable: false,
        filterable: false,
      },
      {
        field: "name",
        headerName: "Event Name",
        flex: 1.5,
        minWidth: 250,
        renderCell: (params) => {
          const startDate = parseEventDate(params.row.startDate);
          return (
            <Box display="flex" alignItems="center">
              <Card sx={{ height: 60, maxWidth: 100, mr: 2 }}>
                <CardMedia
                  component="img"
                  height="60"
                  image="https://d2xqcdy5rl17k2.cloudfront.net/images/default-landing-banner-min.png"
                  alt="Event banner"
                />
              </Card>
              <Box>
                <Typography variant="subtitle1" fontWeight="medium">
                  {params.row.name}
                </Typography>
                <Typography variant="body2" color="textSecondary" component="p">
                  {params.row.about
                    ? stripHtml(params.row.about).substring(0, 60) +
                      (stripHtml(params.row.about).length > 60 ? "..." : "")
                    : "No description"}
                </Typography>
              </Box>
            </Box>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        renderCell: (params) => {
          const startDate = parseEventDate(params.row.startDate);
          const endDate = parseEventDate(params.row.endDate);
          const now = new Date();
          let status;
          if (now < startDate) status = "upcoming";
          else if (now > endDate) status = "completed";
          else status = "ongoing";

          return (
            <Chip
              size="small"
              label={status}
              sx={{
                backgroundColor:
                  status === "upcoming"
                    ? green[500]
                    : status === "ongoing"
                    ? orange[700]
                    : "#888",
                color: "white",
              }}
            />
          );
        },
      },
      {
        field: "date",
        headerName: "Date & Time",
        width: 200,
        renderCell: (params) => {
          console.log("kkkk uuiioo", params.row.name, params.row.startDate);
          const { startDate, endDate, startTime, endTime, timezone } =
            params.row;

          // Convert string timestamp to number
          const startUtc = new Date(Number(startDate));
          const endUtc = new Date(Number(endDate));

          // Combine date + time in the event timezone
          const [startHour, startMinute, startSecond] = startTime
            .split(":")
            .map(Number);
          const [endHour, endMinute, endSecond] = endTime
            .split(":")
            .map(Number);

          // Convert UTC date → zoned date
          let startZoned = utcToZonedTime(startUtc, timezone);
          let endZoned = utcToZonedTime(endUtc, timezone);

          // Override time with startTime/endTime
          startZoned.setHours(startHour, startMinute, startSecond);
          endZoned.setHours(endHour, endMinute, endSecond);

          // Format
          const formattedDate = format(startZoned, "EEE, MMM dd yyyy");
          const formattedStartTime = format(startZoned, "hh:mm a");
          const formattedEndTime = format(endZoned, "hh:mm a");

          return (
            <div>
              <div>{formattedDate}</div>
              <div>
                {formattedStartTime} - {formattedEndTime}
              </div>
            </div>
          );
        },
      },
      {
        field: "participants",
        headerName: "Participants",
        width: 120,
        valueGetter: (params) => {
          // If attendees is an array, return the count
          if (Array.isArray(params.row.attendees)) {
            return params.row.attendees.length || "-";
          }
          // If attendees is a number, return it directly
          if (typeof params.row.attendees === "number") {
            return params.row.attendees;
          }
          return "-";
        },
      },
    ],
    []
  );

  // Event handlers
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = events.map((n) => n.id);
      setSelected(newSelecteds);
    } else {
      setSelected([]);
    }
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  // Get the selected event objects
  const selectedEvents = events.filter((event) => selected.includes(event.id));

  // Custom toolbar actions
  const customToolbarActions = [];

  // Custom actions based on selection
  const getCustomActions = () => {
    if (selected.length === 1) {
      return [
        <Tooltip key="view" title="View">
          <IconButton
            aria-label="View"
            size="large"
            onClick={() =>
              navigate(`/events/view-event/${selectedEvents[0].id}`)
            }
            sx={{ mr: 1 }}
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>,
      ];
    }
    return [];
  };

  return (
    <ReusableTable
      data={events}
      columns={columns}
      searchQuery={searchQuery}
      onSearchChange={onSearchChange}
      searchPlaceholder="Search events..."
      searchFields={["name", "about"]}
      selected={selected}
      onSelectAllClick={handleSelectAllClick}
      onSelect={handleClick}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={handleChangePage}
      onRowsPerPageChange={handleChangeRowsPerPage}
      viewStyle={viewStyle}
      setViewStyle={setViewStyle}
      hasCardView={true}
      CardComponent={EventCard}
      cardProps={{ xs: 12, sm: 6, md: 4 }}
      onBulkEdit={
        selected.length === 1
          ? () => {
              const event = selectedEvents[0];
              navigate(`/events/edit-event/${event.id}`, {
                state: { event },
              });
            }
          : null
      }
      onBulkDelete={() => handleDeleteEvents(selectedEvents)}
      customActions={[...customToolbarActions, ...getCustomActions()]}
      title=""
      showSearch={true}
      loading={loading}
    />
  );
}

function Events() {
  const location = useLocation();
  const { currentSpace } = useSpace();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewStyle, setViewStyle] = useState("List");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    events: [],
  });

  // Get spaceId from the context or URL
  const queryParams = new URLSearchParams(location.search);
  const spaceId = queryParams.get("spaceId") || currentSpace?.id;

  // Fetch events from API
  const fetchEvents = async () => {
    try {
      setLoading(true);

      // Only proceed if we have a spaceId
      if (!spaceId) {
        throw new Error(
          "No space selected. Please select a space to view events."
        );
      }

      const response = await axiosInstance.post("/graphql", {
        query: `
          query GetEvents($spaceId: ID!) {
            getAllEvents(spaceId: $spaceId) {
              id
              name
              startDate
              endDate
              startTime
              endTime
              timezone
              about
              organizedBy
              supportEmail
              attendees {
                id
              }
            }
          }
        `,
        variables: {
          spaceId,
        },
      });

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      setEvents(response.data.getAllEvents || []);
    } catch (error) {
      console.error("Failed to fetch events:", error);
      handleNotification(
        "Failed to fetch events: " + (error.message || "Unknown error"),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Page and row handling
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Search handling
  const handleSearchChange = (event) => {
    const newValue = event.target.value;
    console.log("Events search query changed:", `"${newValue}"`);
    setSearchQuery(newValue);
    setPage(0); // Reset to first page when search changes
  };

  // Notification handling
  const handleNotification = (message, severity = "info") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  // Event deletion
  const handleDeleteEvents = (eventsToDelete) => {
    setConfirmDelete({
      open: true,
      events: eventsToDelete,
    });
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      const deletePromises = confirmDelete.events.map((event) =>
        axiosInstance.post("/graphql", {
          query: `
            mutation DeleteEvent($id: ID!) {
              deleteEvent(id: $id)
            }
          `,
          variables: { id: event.id },
        })
      );

      await Promise.all(deletePromises);

      // Remove deleted events from state
      const eventIdsToDelete = confirmDelete.events.map((event) => event.id);
      setEvents(events.filter((event) => !eventIdsToDelete.includes(event.id)));
      setSelected([]);

      handleNotification(
        `Successfully deleted ${confirmDelete.events.length} event(s)`,
        "success"
      );
    } catch (error) {
      console.error("Error deleting events:", error);
      handleNotification("Failed to delete events", "error");
    } finally {
      setLoading(false);
      setConfirmDelete({ open: false, events: [] });
    }
  };

  // Fetch events when the component mounts or when spaceId changes
  useEffect(() => {
    if (spaceId) {
      fetchEvents();
    }
  }, [spaceId]);

  // Check for state passed from navigation
  useEffect(() => {
    if (location.state?.message) {
      handleNotification(
        location.state.message,
        location.state.severity || "success"
      );
    }
  }, [location.state]);

  return (
    <React.Fragment>
      <Helmet title="Events List" />

      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Events List
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/events/dashboard">
              Dashboard
            </Link>
            <Typography>Events List</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            component={Link}
            color="primary"
            to="/events/add-event"
            startIcon={<AddIcon />}
          >
            New Event
          </Button>
        </Grid>
      </Grid>

      <Divider my={6} />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <EventsTable
            events={events}
            loading={loading}
            viewStyle={viewStyle}
            setViewStyle={setViewStyle}
            selected={selected}
            setSelected={setSelected}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleNotification={handleNotification}
            handleDeleteEvents={handleDeleteEvents}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
          />
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, events: [] })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDelete.events.length === 1
              ? `Are you sure you want to delete the event "${confirmDelete.events[0]?.name}"?`
              : `Are you sure you want to delete ${confirmDelete.events.length} selected events?`}
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ open: false, events: [] })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}

export default Events;
